/**
 * Lookup do pedido de ORIGEM (oneuniformes) para o vínculo cross-tenant.
 *
 * O vendedor digita orc_id + oit_id do pedido que já existe no tenant de origem;
 * este serviço busca esse pedido para confirmação visual antes de criar o pedido
 * no destino (one1print) e gravar o vínculo.
 *
 * Detalhe crítico: quando um cliente roteado está ativo, o `api()` herda o
 * token/tenant do DESTINO automaticamente. Aqui forçamos o token GLOBAL da
 * sessão + o tenant de ORIGEM (override explícito), para a busca bater na
 * oneuniformes, não no destino.
 */

import { api } from "../../../../lib/api";
import { TokenService } from "../../../../lib/tokenService";
import { sendToTerminal } from "../../../../lib/terminalLogger";
import { LogCategory } from "../../../../config/logging";
import { QuotationOperationContextStore } from "./quotationOperationContext";

export interface SourceOrderItemLookup {
	/** true quando o orc_id foi encontrado no tenant de origem. */
	orderFound: boolean;
	/** true quando o oit_id informado pertence ao orc_id encontrado. */
	itemFound: boolean;
	orcId: number;
	oitId: number;
	clientName: string | null;
	productName: string | null;
	/** Produto comercial vinculado (ex.: CAMISETA SUBLIMADA da IMPRESSÃO). */
	linkedProductName: string | null;
	/** oit_id do item comercial vinculado (desambigua múltiplas camisetas). */
	linkedOitId: number | null;
	quantidade: number | null;
	valorTotalItem: number | null;
	valorTotalPedido: number | null;
}

interface RawOrcamentoItem {
	oit_id?: number;
	oit_qtde?: number | string | null;
	oit_valor_total?: number | string | null;
	produto?: { pro_nome?: string | null; pro_id?: number | null } | null;
	linked_context?: {
		final_product?: { pro_nome?: string | null } | null;
		final_oit_id?: number | null;
	} | null;
}

interface RawOrcamento {
	orc_id?: number;
	orc_valor_total?: number | string | null;
	cliente?: { cli_nome?: string | null; cli_nome_fantasia?: string | null } | null;
	orcamento_itens?: RawOrcamentoItem[] | null;
}

function toNumberOrNull(value: number | string | null | undefined): number | null {
	if (value === null || value === undefined) return null;
	const parsed = typeof value === "number" ? value : Number(value);
	return Number.isFinite(parsed) ? parsed : null;
}

/** Um item do pedido de origem, para o select do construtor de vínculo N->1. */
export interface SourceOrderItem {
	oitId: number;
	proId: number | null;
	productName: string | null;
	linkedProductName: string | null;
	linkedOitId: number | null;
	quantidade: number | null;
	valorTotalItem: number | null;
}

/** Pedido de origem com todos os itens (lookup por orc_id apenas). */
export interface SourceOrderLookup {
	orderFound: boolean;
	orcId: number;
	clientName: string | null;
	valorTotalPedido: number | null;
	items: SourceOrderItem[];
}

/**
 * Busca o pedido de origem por orc_id e retorna TODOS os itens, para o vendedor
 * selecionar quais contribuem no vínculo N->1 (SPEC-15 RN-04). Mesma regra de
 * override do lookupSourceOrder: token global + tenant de origem.
 */
export async function lookupSourceOrderItems(
	orcId: number,
): Promise<SourceOrderLookup> {
	const context = QuotationOperationContextStore.get();
	const sourceTenantId = context?.sourceTenantId ?? null;

	if (!sourceTenantId) {
		throw new Error(
			"Contexto de origem indisponível — selecione o cliente roteado novamente.",
		);
	}

	sendToTerminal(
		"🔎 [CROSS_TENANT] Buscando itens do pedido de origem",
		{ sourceTenantId, orcId },
		LogCategory.BUSINESS,
	);

	let raw: RawOrcamento | null;
	try {
		raw = (await api(
			`/orcamento/${orcId}?withLinks=1`,
			{ method: "GET" },
			{
				accessTokenOverride: TokenService.getAccessToken() ?? undefined,
				tenantIdOverride: sourceTenantId,
			},
		)) as RawOrcamento | null;
	} catch (error) {
		if ((error as { status?: number })?.status === 404) {
			return { orderFound: false, orcId, clientName: null, valorTotalPedido: null, items: [] };
		}
		throw error;
	}

	if (!raw || raw.orc_id == null) {
		return { orderFound: false, orcId, clientName: null, valorTotalPedido: null, items: [] };
	}

	const itens = Array.isArray(raw.orcamento_itens) ? raw.orcamento_itens : [];

	return {
		orderFound: true,
		orcId: Number(raw.orc_id),
		clientName: raw.cliente?.cli_nome_fantasia ?? raw.cliente?.cli_nome ?? null,
		valorTotalPedido: toNumberOrNull(raw.orc_valor_total),
		items: itens.map((it) => ({
			oitId: Number(it.oit_id),
			proId: it.produto?.pro_id ?? null,
			productName: it.produto?.pro_nome ?? null,
			linkedProductName: it.linked_context?.final_product?.pro_nome ?? null,
			linkedOitId:
				typeof it.linked_context?.final_oit_id === "number"
					? it.linked_context.final_oit_id
					: null,
			quantidade: toNumberOrNull(it.oit_qtde),
			valorTotalItem: toNumberOrNull(it.oit_valor_total),
		})),
	};
}

/**
 * Busca o pedido de origem por orc_id e localiza o item por oit_id.
 * Lança erro só em falha de rede/servidor; "não encontrado" volta em `orderFound`.
 */
export async function lookupSourceOrder(params: {
	orcId: number;
	oitId: number;
}): Promise<SourceOrderItemLookup> {
	const context = QuotationOperationContextStore.get();
	const sourceTenantId = context?.sourceTenantId ?? null;

	if (!sourceTenantId) {
		throw new Error(
			"Contexto de origem indisponível — selecione o cliente roteado novamente.",
		);
	}

	sendToTerminal(
		"🔎 [CROSS_TENANT] Buscando pedido de origem",
		{ sourceTenantId, orcId: params.orcId, oitId: params.oitId },
		LogCategory.BUSINESS,
	);

	// Override explícito: token global (sessão da origem) + tenant de origem.
	// Passar override desliga a herança automática do contexto de operação (destino).
	let raw: RawOrcamento | null;
	try {
		raw = (await api(
			`/orcamento/${params.orcId}?withLinks=1`,
			{ method: "GET" },
			{
				accessTokenOverride: TokenService.getAccessToken() ?? undefined,
				tenantIdOverride: sourceTenantId,
			},
		)) as RawOrcamento | null;
	} catch (error) {
		// 404 = pedido inexistente na origem (não é erro de fluxo).
		if ((error as { status?: number })?.status === 404) {
			return {
				orderFound: false,
				itemFound: false,
				orcId: params.orcId,
				oitId: params.oitId,
				clientName: null,
				productName: null,
				linkedProductName: null,
				linkedOitId: null,
				quantidade: null,
				valorTotalItem: null,
				valorTotalPedido: null,
			};
		}
		throw error;
	}

	if (!raw || raw.orc_id == null) {
		return {
			orderFound: false,
			itemFound: false,
			orcId: params.orcId,
			oitId: params.oitId,
			clientName: null,
			productName: null,
			quantidade: null,
			valorTotalItem: null,
			valorTotalPedido: null,
		};
	}

	const itens = Array.isArray(raw.orcamento_itens) ? raw.orcamento_itens : [];
	const item = itens.find((it) => Number(it.oit_id) === Number(params.oitId));

	return {
		orderFound: true,
		itemFound: Boolean(item),
		orcId: Number(raw.orc_id),
		oitId: params.oitId,
		clientName: raw.cliente?.cli_nome_fantasia ?? raw.cliente?.cli_nome ?? null,
		productName: item?.produto?.pro_nome ?? null,
		linkedProductName: item?.linked_context?.final_product?.pro_nome ?? null,
		linkedOitId:
			typeof item?.linked_context?.final_oit_id === "number"
				? item.linked_context.final_oit_id
				: null,
		quantidade: toNumberOrNull(item?.oit_qtde),
		valorTotalItem: toNumberOrNull(item?.oit_valor_total),
		valorTotalPedido: toNumberOrNull(raw.orc_valor_total),
	};
}
